import appPrintCustomizerBase from '@geoblocks/mapfishprint/src/BaseCustomizer.ts';
import olGeomPoint from 'ol/geom/Point.js';
import {containsCoordinate} from "ol/extent.js";
import {buffer as bufferExtent} from 'ol/extent';


const EXCLUDE_LAYERS = ['photo_layer'];

const POINT_GEOMETRY_PIXEL_BUFFER = 50;

/**
 * Add a buffer in pixels, given an extent in meters and a resolution in pixels per meter
 * @param {ol.Extent} extent
 * @param {number} resolution
 * @param {number} buffer
 * @return {ol.Extent}
 */
export function addBuffer(extent, resolution, buffer) {
  const bufferMeters = buffer * resolution;
  return bufferExtent(extent, bufferMeters);
}


export default class PrintCustomizer extends appPrintCustomizerBase {
  /**
   * @param {number} resolution
   * @param {ol.Extent} extent
   * @param {Array<ol.layer.Vector>} detourLayers
   */
  constructor(options) {
    super();
    const {
      resolution,
      extent,
      detourLayers,
      HIGHLIGHT_SPACING,
      HIGHLIGHT_SPACING_SMALL,
      HIGHLIGHT_WIDTH,
      HIGHLIGHT_WIDTH_SMALL,
      PIXELS_PER_POINT,
      resolutionDependentURL
    } = options;

    this.HIGHLIGHT_SPACING = HIGHLIGHT_SPACING;
    this.HIGHLIGHT_SPACING_SMALL = HIGHLIGHT_SPACING_SMALL;
    this.HIGHLIGHT_WIDTH = HIGHLIGHT_WIDTH;
    this.HIGHLIGHT_WIDTH_SMALL = HIGHLIGHT_WIDTH_SMALL;
    this.PIXELS_PER_POINT = PIXELS_PER_POINT;
    this.resolutionDependentURL = resolutionDependentURL;

    /**
     * @private
     */
    this.isSmallResolution_ = (resolution <= 20);

    /**
     * @private
     */
    this.resolution_ = resolution;

    /**
     * @private
     */
    this.bufferedExtent_ = addBuffer(extent, resolution, POINT_GEOMETRY_PIXEL_BUFFER);

    this.printExtent = extent;

    /**
     * @private
     */
    this.detourLayers_ = detourLayers;
  }

  /**
   * @override
   */
  layerFilter(layer) {
    return !EXCLUDE_LAYERS.includes(layer.get('type'));
  }

  /**
   * @override
   */
  geometryFilter(geometry) {
    if (geometry instanceof olGeomPoint) {
      const point = /** @type {ol.geom.Point} */ (geometry);
      return this.isWithinBufferedExtent_(point);
    }
    return true;
  }

  /**
   * @override
   */
  feature(layer, feature) {
    // remove all the unneeded features properties from the print spec
    const properties = feature.properties;
    for (const key in properties) {
      if (!key.includes('ngeo_style')) {
        delete properties[key];
      }
    }
  }

  /**
   * @override
   */
  line(layer, symbolizer, stroke) {
    console.assert(symbolizer);

    if (this.isTrackLayer_(layer) || this.isDetourLayer_(layer) || this.isMVTRouteLayer_(layer) || this.isSlowupLayer_(layer)) {
      // Change symbolizers stroke width to 2.5 (client rule)
      symbolizer['strokeWidth'] = 2.5;
    }

    if ('strokeDashstyle' in symbolizer) {
      if (this.isHighlightLayer_(layer)) {
        symbolizer['strokeDashstyle'] = this.highlightStrokeDashstyle_();
        symbolizer['strokeLinecap'] = 'round';
        symbolizer['strokeOpacity'] = 0.8;
        symbolizer['strokeWidth'] = this.highlightStrokeWidth_();
      } else if (this.isDetourLayer_(layer) || this.isSlowupLayer_(layer)) {
        symbolizer['strokeDashstyle'] = this.strokeDashstyle_(symbolizer['strokeDashstyle']);
        symbolizer['strokeLinecap'] = 'round';
      }
    }
  }

  /**
   * @private
   * @return {string}
   */
  highlightStrokeDashstyle_() {
    const dotSpacingPixels = this.isSmallResolution_ ? this.HIGHLIGHT_SPACING_SMALL : this.HIGHLIGHT_SPACING;
    const dotSpacing = dotSpacingPixels / this.PIXELS_PER_POINT;
    return '0.001 ' + dotSpacing.toFixed(3);
  }

  /**
   * @private
   * @param {string} dash
   * @return {string}
   */
  strokeDashstyle_(dash) {
    return dash.split(' ').map(v => parseFloat(v) / this.PIXELS_PER_POINT).join(' ');
  }

  /**
   * @private
   * @return {number}
   */
  highlightStrokeWidth_() {
    const strokeWidthPixels = this.isSmallResolution_ ? this.HIGHLIGHT_WIDTH_SMALL : this.HIGHLIGHT_WIDTH;
    return strokeWidthPixels / this.PIXELS_PER_POINT;
  }

  /**
   * @override
   */
  point(layer, symbolizer, image) {
    if (symbolizer['externalGraphic']) {
      // Remove cache-busting suffix: Icons are loaded from the filesystem in
      // mapfish-print, so it is not required and would not work.
      symbolizer['externalGraphic'] = symbolizer['externalGraphic'].split('?')[0];
    }
    if (symbolizer['graphicWidth']) {
      if (this.isTrackLayer_(layer)) {
        // Make POIs bigger
        symbolizer['graphicWidth'] = 20;
        symbolizer['graphicYOffset'] = -35;
      } else {
        symbolizer['graphicWidth'] /= this.PIXELS_PER_POINT;
      }
    }
  }

  /**
   * @override
   */
  wmtsLayer(layer, wmtsLayer, source) {
    wmtsLayer.baseURL = this.resolutionDependentURL(wmtsLayer.baseURL, this.resolution_);
  }

  /**
   * @param {ol.layer.Vector} layer
   * @return {boolean}
   * @private
   */
  isDetourLayer_(layer) {
    return this.detourLayers_.includes(layer);
  }

  /**
   * @param {ol.layer.Vector|ol.layer.VectorTile} layer
   * @return {boolean}
   * @private
   */
  isMVTRouteLayer_(layer) {
    return layer.get('kind') === 'mvt_route_layer';
  }

  /**
   * @param {ol.layer.Vector} layer
   * @return {boolean}
   * @private
   */
  isTrackLayer_(layer) {
    return layer.get('type') === 'selected_tracks';
  }

  /**
   * @param {ol.layer.Vector} layer
   * @return {boolean}
   * @private
   */
  isSlowupLayer_(layer) {
    return layer.get('layername') === 'Slowup';
  }

  /**
   * @param {ol.layer.Vector} layer
   * @return {boolean}
   * @private
   */
  isHighlightLayer_(layer) {
    return layer.get('name') === 'highlight';
  }

  /**
   * @param {ol.geom.Point} point
   * @return {boolean}
   * @private
   */
  isWithinBufferedExtent_(point) {
    const coordinates = point.getCoordinates();
    return containsCoordinate(this.bufferedExtent_, coordinates);
  }
}
